import { marked } from 'marked';

export type TableData = {
  columns: string[];
  rows: string[][];
  cellAttrs?: Array<Array<Record<string, unknown> | undefined>>;
};

interface EditorJSONNode {
  attrs?: Record<string, unknown>;
  content?: EditorJSONNode[];
  marks?: unknown[];
  text?: string;
  type?: string;
}

function hasMeaningfulAttrs(
  attrs: Record<string, unknown> | undefined,
  allowed: Set<string> = new Set()
): boolean {
  return Object.entries(attrs ?? {}).some(
    ([key, value]) =>
      !allowed.has(key) &&
      value !== null &&
      value !== undefined &&
      value !== false
  );
}

function readEditorCell(
  cell: EditorJSONNode,
  header: boolean
): { attrs?: Record<string, unknown>; text: string } | null {
  if (cell.type !== (header ? 'tableHeader' : 'tableCell')) return null;
  const attrs = cell.attrs ?? {};
  if (Number(attrs.colspan ?? 1) !== 1 || Number(attrs.rowspan ?? 1) !== 1) {
    return null;
  }
  if (
    hasMeaningfulAttrs(
      attrs,
      new Set([
        'backgroundColor',
        'colspan',
        'colwidth',
        'headerStyled',
        'rowspan',
      ])
    )
  ) {
    return null;
  }

  const content = cell.content ?? [];
  if (content.length !== 1 || content[0]?.type !== 'paragraph') return null;
  const paragraph = content[0];
  if (hasMeaningfulAttrs(paragraph.attrs)) return null;

  const inline = paragraph.content ?? [];
  if (
    inline.some(
      (node) =>
        node.type !== 'text' ||
        typeof node.text !== 'string' ||
        (node.marks?.length ?? 0) > 0
    )
  ) {
    return null;
  }
  return {
    ...(cell.attrs ? { attrs: { ...cell.attrs } } : {}),
    text: inline.map((node) => node.text).join(''),
  };
}

function readEditorTable(table: EditorJSONNode): TableData | null {
  if (hasMeaningfulAttrs(table.attrs)) return null;
  const rows = table.content ?? [];
  if (rows.length === 0) return null;

  const parsedRows: Array<
    Array<{ attrs?: Record<string, unknown>; text: string }>
  > = [];
  for (const [rowIndex, row] of rows.entries()) {
    if (row.type !== 'tableRow' || hasMeaningfulAttrs(row.attrs)) return null;
    const parsed = (row.content ?? []).map((cell) =>
      readEditorCell(cell, rowIndex === 0)
    );
    if (parsed.length === 0 || parsed.some((cell) => cell === null))
      return null;
    parsedRows.push(
      parsed as Array<{ attrs?: Record<string, unknown>; text: string }>
    );
  }

  const columns = parsedRows[0]!;
  if (parsedRows.some((row) => row.length !== columns.length)) return null;
  const cellAttrs = parsedRows.map((row) => row.map((cell) => cell.attrs));
  return {
    columns: columns.map((cell) => cell.text),
    rows: parsedRows.slice(1).map((row) => row.map((cell) => cell.text)),
    ...(cellAttrs.some((row) => row.some(Boolean)) ? { cellAttrs } : {}),
  };
}

function editorCell(
  type: 'tableHeader' | 'tableCell',
  text: string,
  attrs?: Record<string, unknown>
) {
  return {
    type,
    ...(attrs ? { attrs } : {}),
    content: [
      {
        type: 'paragraph',
        ...(text ? { content: [{ type: 'text', text }] } : {}),
      },
    ],
  };
}

function editorTable(data: TableData): EditorJSONNode {
  return {
    type: 'table',
    content: [
      {
        type: 'tableRow',
        content: data.columns.map((text, column) =>
          editorCell('tableHeader', text, data.cellAttrs?.[0]?.[column])
        ),
      },
      ...data.rows.map((row, rowIndex) => ({
        type: 'tableRow',
        content: row.map((text, column) =>
          editorCell(
            'tableCell',
            text,
            data.cellAttrs?.[rowIndex + 1]?.[column]
          )
        ),
      })),
    ],
  };
}

export function findEditorTables(document: unknown): Array<TableData | null> {
  const tables: Array<TableData | null> = [];
  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') return;
    const node = value as EditorJSONNode;
    if (node.type === 'table') {
      tables.push(readEditorTable(node));
      return;
    }
    node.content?.forEach(visit);
  };
  visit(document);
  return tables;
}

export function replaceEditorTable(
  document: unknown,
  tableIndex: number,
  data: TableData
): unknown {
  let currentIndex = 0;
  const visit = (value: unknown): unknown => {
    if (!value || typeof value !== 'object') return value;
    const node = value as EditorJSONNode;
    if (node.type === 'table') {
      return currentIndex++ === tableIndex ? editorTable(data) : value;
    }
    if (!node.content) return value;
    const content = node.content.map(visit) as EditorJSONNode[];
    return content.some((child, index) => child !== node.content?.[index])
      ? { ...node, content }
      : value;
  };
  return visit(document);
}

export interface MarkdownTableMatch {
  data: TableData | null;
  end: number;
  source: string;
  start: number;
}

function escapeCell(value: string): string {
  return value
    .replace(/\r?\n/g, ' ')
    .trim()
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|');
}

export function toMarkdownTable({ columns, rows }: TableData): string {
  const header = `| ${columns.map(escapeCell).join(' | ')} |`;
  const divider = `| ${columns.map(() => '---').join(' | ')} |`;
  const body = rows
    .map((row) => `| ${row.map(escapeCell).join(' | ')} |`)
    .join('\n');
  return `${header}\n${divider}\n${body}`;
}

const escapedHyphenPattern = /(^|[^\\])((?:\\\\)*)\\-/g;

function normalizeEscapedHyphens(value: string): string {
  return value.replace(escapedHyphenPattern, '$1$2-');
}

function decodeCell(value: string): string {
  return normalizeEscapedHyphens(value.trim()).replace(/\\\\/g, '\\');
}

function splitRow(line: string): string[] {
  let source = line.trim();
  if (source.startsWith('|')) source = source.slice(1);
  if (source.endsWith('|')) source = source.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  for (const character of source) {
    if (character !== '|') {
      cell += character;
      continue;
    }
    let backslashes = 0;
    for (let index = cell.length - 1; cell[index] === '\\'; index -= 1) {
      backslashes += 1;
    }
    if (backslashes % 2 === 1) {
      cell = `${cell.slice(0, -1)}|`;
    } else {
      cells.push(decodeCell(cell));
      cell = '';
    }
  }
  cells.push(decodeCell(cell));
  return cells;
}

function isDividerRow(cells: string[]): boolean {
  return cells.every((cell) => /^-{1,}$/.test(cell.trim()));
}

export function parseMarkdownTable(markdown: string): TableData | null {
  const source = markdown.trim();
  const lines = source.split('\n');
  if (
    lines.length < 2 ||
    lines.some(
      (line) =>
        line !== line.trim() || !line.startsWith('|') || !line.endsWith('|')
    )
  ) {
    return null;
  }

  const columns = splitRow(lines[0]);
  const divider = splitRow(lines[1]);
  if (
    columns.length === 0 ||
    divider.length !== columns.length ||
    !isDividerRow(divider)
  ) {
    return null;
  }
  const rows = lines.slice(2).map(splitRow);
  if (rows.some((row) => row.length !== columns.length)) return null;

  return { columns, rows };
}

export function findMarkdownTables(markdown: string): MarkdownTableMatch[] {
  const matches: MarkdownTableMatch[] = [];
  let offset = 0;

  for (const token of marked.lexer(markdown)) {
    const start = markdown.startsWith(token.raw, offset)
      ? offset
      : markdown.indexOf(token.raw, offset);
    if (start < 0) return [];
    offset = start + token.raw.length;
    if (token.type !== 'table') continue;
    const raw = token.raw.replace(/\n+$/, '');
    matches.push({
      data: parseMarkdownTable(raw),
      end: start + raw.length,
      source: raw,
      start,
    });
  }

  return matches;
}

export function replaceMarkdownTable(
  markdown: string,
  tableIndex: number,
  data: TableData
): string {
  const match = findMarkdownTables(markdown)[tableIndex];
  if (!match) return markdown;
  return `${markdown.slice(0, match.start)}${toMarkdownTable(data)}${markdown.slice(match.end)}`;
}
